import ChipGroup from './ChipGroup';

/**
 * Structurally the same as `lib/onboarding/steps.Field`, declared here so the
 * component keeps its promise of importing nothing from lib. A `Field` is
 * assignable to it, and the compiler still catches a drift between the two.
 */
export interface FieldShape {
  key: string;
  label: string;
  kind: 'text' | 'textarea' | 'tel' | 'url' | 'single' | 'multi';
  hint?: string;
  placeholder?: string;
  options?: readonly string[];
  max?: number;
  xp: number;
  optional?: boolean;
}

interface FieldRowProps {
  field: FieldShape;
  value: string | string[] | undefined;
  answered: boolean;
  onChange: (next: string | string[]) => void;
}

/**
 * One question, with its XP on the right. The number is the whole game: it turns
 * "another field" into a visible price, and it is why the optional ones still
 * get filled in.
 */
export default function FieldRow({ field, value, answered, onChange }: FieldRowProps) {
  const chips = field.kind === 'single' || field.kind === 'multi';
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const text = typeof value === 'string' ? value : '';
  const id = `f-${field.key}`;

  return (
    <div className="field">
      <div className="fl">
        {chips ? (
          <span className="fn">{field.label}</span>
        ) : (
          <label className="fn" htmlFor={id}>
            {field.label}
          </label>
        )}
        <span className={`fx ${answered ? 'got' : ''}`}>
          {answered ? '✓ ' : ''}
          {field.xp} XP{field.optional ? ' · optional' : ''}
        </span>
      </div>

      {field.hint ? <p className="hint">{field.hint}</p> : null}

      {chips ? (
        <ChipGroup
          options={field.options || []}
          selected={selected}
          multi={field.kind === 'multi'}
          max={field.max}
          onChange={next => onChange(field.kind === 'multi' ? next : next[0] || '')}
        />
      ) : field.kind === 'textarea' ? (
        <textarea
          id={id}
          className="inp"
          value={text}
          placeholder={field.placeholder}
          onChange={event => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="inp"
          type={field.kind === 'tel' ? 'tel' : field.kind === 'url' ? 'url' : 'text'}
          value={text}
          placeholder={field.placeholder}
          autoComplete={autoCompleteFor(field.key)}
          onChange={event => onChange(event.target.value)}
        />
      )}
    </div>
  );
}

function autoCompleteFor(key: string): string {
  if (key === 'full_name' || key === 'contact_person') return 'name';
  if (key === 'phone') return 'tel';
  if (key === 'country') return 'country-name';
  if (key === 'city') return 'address-level2';
  if (key === 'company_name') return 'organization';
  if (key === 'designation') return 'organization-title';
  if (key === 'website' || key === 'portfolio_url') return 'url';
  return 'off';
}
